/**
 * Replaying the action a signed-out seeker was blocked from doing.
 *
 * The click used to be discarded: the seeker hearts an image, gets the login
 * modal, registers, and lands somewhere else with the image un-hearted. These
 * handlers re-run it once they are signed in.
 *
 * Constraint that shapes this file: logging in navigates (handleRouteChange
 * re-enters on a clean '/' to re-establish the session), which unmounts the
 * component that raised the gate. So a resume cannot be a callback closed over
 * component state. It is a plain {action, args} descriptor, dispatched here.
 * Only actions that are one API call with no UI dependency belong; see
 * RESUMABLE_ACTIONS in auth-intents.js.
 */
import { RESUMABLE_ACTIONS } from '@/utils/auth-intents'

export const RESUME_HANDLERS = {
  'favorite-image': async (args, { sessionId, api }) => {
    await api.toggleFavoriteImage(sessionId, args.url, {
      owner_uuid: args.ownerUuid || '',
      source: args.source || '',
      title: args.title || '',
    })
    return 'Saved to your collection.'
  },

  'follow-mystic': async (args, { sessionId, api }) => {
    // Always 'follow'. The seeker was signed out, so there is nothing to undo,
    // and a toggle here could silently unfollow after a stale descriptor.
    await api.followReader(args.subdomain, 'follow', sessionId)
    return 'Following.'
  },

  'request-friend': async (args, { sessionId, api }) => {
    await api.sendFriendRequest(args.querentUuid, sessionId)
    return 'Friend request sent.'
  },

  'accept-friend': async (args, { sessionId, api }) => {
    // The live path prompts for a nickname. A resume runs unattended, so accept
    // with a blank alias; the seeker can name them later from their friends list.
    await api.respondFriendRequest(args.querentUuid, 'accept', sessionId, '')
    return 'Friend added.'
  },

  'rate-deck': async (args, { sessionId, api }) => {
    await api.rateDeck(args.subdomain, args.stars, sessionId)
    return 'Your rating is in.'
  },

  'vote-poll': async (args, { sessionId, api }) => {
    await api.castCommunityVote(args.pollKey, args.optionId, sessionId)
    return 'Your vote is counted.'
  },
}

/**
 * Run a stored resume descriptor. Never throws: a failed replay must not break
 * the seeker's landing, since they are already signed in by this point and the
 * action is a bonus, not the transaction.
 *
 * Returns { ok, action, message } or null when there is nothing to replay.
 */
export async function runResume(resume, { sessionId, api }) {
  const action = resume && resume.action
  if (!action || !RESUMABLE_ACTIONS.includes(action)) return null

  const handler = RESUME_HANDLERS[action]
  if (!handler) return null

  try {
    const message = await handler(resume.args || {}, { sessionId, api })
    return { ok: true, action, message }
  } catch (e) {
    console.error(`Resume "${action}" failed after login:`, e)
    return { ok: false, action, message: '' }
  }
}
